import React from 'react';
import { PolicyPage, Section, Bullets, MERCHANT } from './legal';

const DrivelineRefund = () => (
  <PolicyPage
    testid="driveline-refund"
    title="Refund & Cancellation Policy"
    intro="This policy explains how to cancel a Driveline subscription, when a refund is given, and how
           long a refund takes to reach you."
  >
    <Section title="Try before you pay" testid="refund-trial">
      <p>
        Every account starts with a 14-day free trial. No payment details are taken to begin the trial
        and nothing is charged during it. We ask you to use the trial to confirm Driveline suits your
        workshop, because it is the most reliable way to avoid a refund situation altogether.
      </p>
    </Section>

    <Section title="Cancelling your subscription" testid="refund-cancel">
      <Bullets items={[
        'You can cancel at any time from Subscription inside your Driveline account, or by emailing ' + MERCHANT.email + ' from your registered address.',
        'Cancellation stops all future auto-debits. Any UPI AutoPay or eNACH mandate is revoked with the payment gateway.',
        'Your subscription stays fully active until the end of the period you have already paid for — we do not cut off access on the day you cancel.',
        'After that date the account becomes read-only. You keep access to view, search and download everything you created.',
        'There is no cancellation fee, notice period or lock-in on monthly plans.',
      ]} />
    </Section>

    <Section title="Refund eligibility" testid="refund-eligibility">
      <p>Subscription fees are generally non-refundable once a billing period has started, with these exceptions:</p>
      <Bullets items={[
        'Duplicate or double charge — refunded in full, always.',
        'A charge taken after you cancelled, or after a mandate was revoked — refunded in full.',
        'Any charge taken in error by us or the payment gateway — refunded in full.',
        'First annual payment: if you tell us within 7 days of the charge that Driveline is not working for you, we refund it in full.',
        'An extended failure on our side that leaves the service unusable and that we cannot resolve — refunded pro-rata for the affected days.',
      ]} />
      <p>
        Refunds are not given for a period that has already been used, for a change of mind partway
        through a paid month, for accounts suspended for breach of the Terms of Service, or because a
        workshop chose not to use the software it subscribed to.
      </p>
    </Section>

    <Section title="How to request a refund" testid="refund-request">
      <p>
        Email <a className="text-teal-700 underline" href={`mailto:${MERCHANT.email}`}>{MERCHANT.email}</a> from
        the email address registered on the account, with your workshop name, the payment date and the
        amount. If it helps, attach the payment reference from your bank or UPI app.
      </p>
      <Bullets items={[
        'We acknowledge every refund request within 2 working days.',
        'A decision is given within 7 working days of receiving the request.',
        'Approved refunds are initiated within 3 working days of the decision.',
      ]} />
    </Section>

    <Section title="How refunds are paid" testid="refund-method">
      <p>
        Refunds are made only to the original payment method through Cashfree Payments — back to the
        same UPI ID, card or bank account the charge came from. We cannot redirect a refund to a
        different account.
      </p>
      <p>
        Once initiated, the amount typically reaches you in <b>5–7 working days</b>, depending on your
        bank or card issuer. Some issuers take up to 10 working days. We share the refund reference
        number by email as soon as the refund is initiated so you can trace it with your bank.
      </p>
      <p>
        Where GST was charged on the original payment, that GST is refunded along with it and a credit
        note is issued.
      </p>
    </Section>

    <Section title="Your data after cancellation" testid="refund-data">
      <p>
        Cancelling does not delete your records. The account stays read-only so you can export
        invoices, customer lists and documents for your own compliance. If you want the account and
        its data permanently deleted, write to us and we will do so within 30 days of your request,
        apart from records we are required by law to keep (such as GST invoices issued to you).
      </p>
    </Section>

    <Section title="Disputes" testid="refund-disputes">
      <p>
        If you disagree with a refund decision, reply to the same email thread and ask for it to be
        reviewed by the account owner at {MERCHANT.legal}. We would far rather settle it directly than
        have you raise a chargeback, and we respond to every escalation in writing.
      </p>
    </Section>
  </PolicyPage>
);

export default DrivelineRefund;
